import {
  type FilterRule,
  type TextFilterOp,
  type NumberFilterOp,
  type DateFilterOp,
  isRuleEmpty,
} from '@/lib/tableFilters';
import { ALL_COLUMN_DEFS } from '@/hooks/useTableColumns';
import { COLUMN_TITLES } from '@/components/ProjectsTable/FilterPopover';

const TEXT_OP: Record<TextFilterOp, string> = {
  none: 'Не выбран',
  contains: 'Содержит',
  notContains: 'Не содержит',
  equals: 'Равно',
  startsWith: 'Начинается с',
  isEmpty: 'Пусто',
  isNotEmpty: 'Не пусто',
};

const NUM_OP: Record<NumberFilterOp, string> = {
  none: 'Не выбран',
  eq: '=',
  neq: '≠',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
  between: 'Диапазон',
  isEmpty: 'Пусто',
  isNotEmpty: 'Не пусто',
};

const DATE_OP: Record<DateFilterOp, string> = {
  none: 'Не выбран',
  equals: 'Дата',
  before: 'До',
  after: 'После',
  between: 'Период',
  isEmpty: 'Пусто',
  isNotEmpty: 'Не пусто',
};

/** Колонки таблицы проектов для сводки фильтров. */
export function getFilterColumnTitle(columnId: string): string {
  const t = ALL_COLUMN_DEFS[columnId]?.title?.trim();
  if (t && t.length > 0) return t;
  return COLUMN_TITLES[columnId] ?? columnId;
}

export interface FilterSummaryOptions {
  /** Переопределяет подпись колонки (напр. каталог чтецов). */
  getColumnTitle?: (columnId: string) => string;
  /** Отображение id тегов чтеца в тексте фильтра. */
  readerTagIdToLabel?: Record<string, string>;
}

export function formatFilterRuleLine(
  columnId: string,
  rule: FilterRule,
  options?: FilterSummaryOptions
): string {
  const title = options?.getColumnTitle?.(columnId) ?? getFilterColumnTitle(columnId);

  switch (rule.type) {
    case 'text': {
      const parts: string[] = [];
      if (rule.selectedValues.length > 0) {
        parts.push(rule.selectedValues.join(', '));
      }
      if (rule.op !== 'none') {
        if (rule.op === 'isEmpty' || rule.op === 'isNotEmpty') {
          parts.push(TEXT_OP[rule.op]);
        } else if (rule.value.trim()) {
          parts.push(`${TEXT_OP[rule.op]}: ${rule.value.trim()}`);
        }
      }
      return `${title}: ${parts.join('; ')}`;
    }
    case 'select':
      return `${title}: ${rule.values.join(', ')}`;
    case 'multiPerson': {
      const parts: string[] = [];
      if (rule.names.length > 0) {
        parts.push(rule.names.join(', '));
      }
      if (rule.op !== 'none') {
        if (rule.op === 'isEmpty' || rule.op === 'isNotEmpty') {
          parts.push(TEXT_OP[rule.op]);
        } else if (rule.value.trim()) {
          parts.push(`${TEXT_OP[rule.op]}: ${rule.value.trim()}`);
        }
      }
      const body = parts.length > 0 ? parts.join('; ') : '—';
      return `${title}: ${body}`;
    }
    case 'date': {
      if (rule.op === 'isEmpty' || rule.op === 'isNotEmpty') {
        return `${title}: ${DATE_OP[rule.op]}`;
      }
      if (rule.op === 'between' && rule.value2) {
        return `${title}: ${DATE_OP[rule.op]} ${rule.value} — ${rule.value2}`;
      }
      return `${title}: ${DATE_OP[rule.op]} ${rule.value}`;
    }
    case 'number': {
      if (rule.op === 'isEmpty' || rule.op === 'isNotEmpty') {
        return `${title}: ${NUM_OP[rule.op]}`;
      }
      if (rule.op === 'between') {
        return `${title}: ${NUM_OP.between} ${rule.value} — ${rule.value2 ?? '—'}`;
      }
      return `${title}: ${NUM_OP[rule.op]} ${rule.value}`;
    }
    case 'boolean':
      return `${title}: ${rule.value === true ? 'Да' : rule.value === false ? 'Нет' : '—'}`;
    case 'readerTagsFacet': {
      const map = options?.readerTagIdToLabel ?? {};
      const fragments: string[] = [];
      for (const [group, ids] of Object.entries(rule.facets ?? {})) {
        if (!ids?.length) continue;
        fragments.push(`${group}: ${ids.map((id) => map[id] ?? id).join(', ')}`);
      }
      const body = fragments.length > 0 ? fragments.join('; ') : '—';
      return `${title}: ${body}`;
    }
    default:
      return `${title}: —`;
  }
}

export interface ActiveFilterSummaryRow {
  columnId: string;
  line: string;
}

export function getActiveFilterSummaryRows(
  filters: Record<string, FilterRule>,
  options?: FilterSummaryOptions
): ActiveFilterSummaryRow[] {
  return Object.entries(filters)
    .filter(([, r]) => !isRuleEmpty(r))
    .map(([columnId, r]) => ({
      columnId,
      line: formatFilterRuleLine(columnId, r, options),
    }))
    .sort((a, b) => a.line.localeCompare(b.line, 'ru'));
}
